/** Public parish row from GET /v1/parishes (PARISH_PUBLIC_SELECT). */
export interface ParishPublic {
    id: string;
    parentId: string | null;
    name: string;
    code: string | null;
    logoUrl: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    description: string | null;
    campaign: string | null;
    timezone: string | null;
    street1: string | null;
    street2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    country: string | null;
    lat: number | null;
    lon: number | null;
    status: string;
}

/**
 * UI row for parish directory / autocomplete / detail.
 * `companyId` / `companyCode` are aliases for route params and Freeze consumers
 * that still read those keys; values are parish UUID / code from /v1.
 */
export interface Parish extends ParishPublic {
    companyId: string;
    companyCode: string | null;
    webSite: string | null;
    location: string;
    archdiocese: string;
    zip: string | null;
}

/** Normalize a /v1 parish payload for list/detail/autocomplete consumers. */
export function normalizeParish(raw: ParishPublic | Record<string, unknown>): Parish {
    const p = raw as ParishPublic;
    const city = p.city ?? null;
    const state = p.state ?? null;
    const location = [city, state].filter(Boolean).join(', ');
    return {
        id: String(p.id),
        parentId: (p.parentId as string | null) ?? null,
        name: p.name ?? '',
        code: p.code ?? null,
        logoUrl: p.logoUrl ?? null,
        email: p.email ?? null,
        phone: p.phone ?? null,
        website: p.website ?? null,
        description: p.description ?? null,
        campaign: p.campaign ?? null,
        timezone: p.timezone ?? null,
        street1: p.street1 ?? null,
        street2: p.street2 ?? null,
        city,
        state,
        zipCode: p.zipCode ?? null,
        country: p.country ?? null,
        lat: p.lat ?? null,
        lon: p.lon ?? null,
        status: p.status ?? '',
        companyId: String(p.id),
        companyCode: p.code ?? null,
        webSite: p.website ?? null,
        location,
        archdiocese: '',
        zip: p.zipCode ?? null,
    };
}

/** Single-line address for parish detail (flat street fields on /v1 parish). */
export function formatParishAddress(
    parish:
        | {
              street1?: string | null;
              street2?: string | null;
              city?: string | null;
              state?: string | null;
              zipCode?: string | null;
              zip?: string | null;
              country?: string | null;
          }
        | null
        | undefined
): string {
    if (!parish) return '';
    const street = [parish.street1, parish.street2].filter(Boolean).join(' ').trim();
    const cityState = [parish.city, parish.state].filter(Boolean).join(', ');
    const zip = parish.zipCode || parish.zip || '';
    return [street, cityState, zip, parish.country].filter(Boolean).join(', ');
}
